import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, TemplateRef, Output, EventEmitter, ViewChild, SimpleChanges, Injector } from '@angular/core';
import { AbstractControl, ControlContainer, FormBuilder, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { SpinnerComponent } from '../spinner/spinner.component';
import {
  TreeDropDownCallBack,
  TreeDropdownBloc,
  TreeDropdownLoadEvent,
  TreeDropdownReadyState,
  TreeDropdownSearchEvent,
  TreeDropdownSearchReadyState,
  TreeDropdownState,
} from '../../bloc/tree-dropdown-bloc';
import { Subject } from 'rxjs';
import { DropdownModel, Pagination } from 'src/app/core/data/model';
import { DropdownConfigurations, SvgIcon } from '../../interfaces';
import { SpinnerSize } from '../../common';
import { LayoutService } from 'src/app/core/helper';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ErrorState, ProcessingState } from '../../bloc/base-bloc';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DropDownFilterPipe } from '../../pipes/dropdown-filter-pipe';
import { ErrorValidationMessageComponent } from '../error-validation-message/error-validation-message.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { IconSvgComponent } from '../icon/svg-icon.component';


@Component({
  selector: 'app-tree-dropdown',
  standalone: true,
  imports: [
    // modules
    CommonModule,
    MatSelectModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDividerModule,
    TranslateModule,
    MatCheckboxModule,

    // components
    SpinnerComponent,
    IconSvgComponent,

    // pipes
    DropDownFilterPipe,
  ],
  templateUrl: './tree-dropdown.component.html',
  styleUrls: ['./tree-dropdown.component.scss'],
  providers: [TreeDropdownBloc],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class TreeDropdownComponent<T extends DropdownModel> implements OnInit {
  // publics
  search: string;
  form: FormGroup;
  loading: boolean = false;
  formControl: AbstractControl;
  formGroup: FormGroup;

  // privates
  private page: number = 1;
  private limit: number = 30;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private search$: Subject<string> = new Subject<string>();
  private pagination: Pagination;

 // isSelectAllActive = false;

  // inputs
  @Input() list: T[] = [];
  @Input() selectedItems: number[] = [];
  @Input() changeFromOutSide$: Subject<boolean> = new Subject();
  @Input() retrievedElement: T;
  @Input() dropdownConfigurations: DropdownConfigurations;
  @Input() optionCustomTemplate: TemplateRef<any>;
  @Input() enableSearch: boolean = true;
  @Input() caller: TreeDropDownCallBack<any>;
  @Input() dropdownIcon: SvgIcon;

  // outputs
  @Output() searchChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectionChange: EventEmitter<T> = new EventEmitter<T>();

  // viewchilds
  @ViewChild('Select', { static: false }) MatSelect: MatSelect;
  @ViewChild('input', { static: false }) searchInput;

  //enums
  SpinnerSize = SpinnerSize;

  constructor(
    public layoutService: LayoutService, 
    private formBuilder: FormBuilder, 
    private bloc: TreeDropdownBloc,
    private injector: Injector) {
    this.initializeForm();
    this.bloc.onState.pipe(takeUntil(this.destroy$)).subscribe((state) => this.onHandleBlocStates(state));
  }

  // life cycle hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['selectedItems']) {
      this.selectedItems = changes['selectedItems'].currentValue;
    }
    if (changes && changes['list']) {
      this.list = changes['list'].currentValue;
      this.getSelectedItems();
    }
  }
  ngOnInit(): void {
    this.loadDropdownData();
    this.checkIfThereIsCallerChangeFromOutSide();
    this.debounceSearchText();
    this.getFormControl();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  // publics
 /*  toggleAllSelection(): void {
    this.isSelectAllActive = !this.isSelectAllActive;
    if (this.isSelectAllActive) {
       this.MatSelect.options.forEach((item: MatOption) => item.select())
    } else {
      this.MatSelect.options.forEach((item: MatOption) => item.deselect())
    }
  } */

  onSelectionChange(selectChange: MatSelectChange): void {
    this.selectedItems = selectChange.value;
    this.selectionChange.emit(this.list.find((item) => item['id'] == selectChange.value));
  }

  onOpenedChange(event: boolean): void {
    if (event) {
      this.MatSelect.panel?.nativeElement.addEventListener('scroll', (event: any) => {
        if (
          this.MatSelect?.panel?.nativeElement.scrollTop >=
          this.MatSelect?.panel?.nativeElement.scrollHeight - this.MatSelect?.panel?.nativeElement.offsetHeight
        ) {
          if (this.page <= this.pagination?.total / this.limit) {
            this.page = this.page + 1;
            this.bloc.setEvent.next(new TreeDropdownLoadEvent(this.caller, this.page, this.limit, this.search));
          }
        }
      });
    }
  }

  onSearch(event: Event): void {
    let target = event.target as HTMLInputElement;
    this.search = target.value;
  }

  // privates

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      treeSelect: [],
    });
  }

  private debounceSearchText(): void {
    this.search$.pipe(debounceTime(1000)).subscribe((res) => {
      this.bloc.setEvent.next(new TreeDropdownSearchEvent(this.caller, (this.page = 1), this.limit, this.search));
    });
  }

  private loadDropdownData(): void {
    if (this.caller) {
      this.bloc.setEvent.next(new TreeDropdownLoadEvent(this.caller, this.page, this.limit, this.search));
    }
  }

  private getSelectedItems(): void {
    this.selectedItems.forEach((selectedId) => {
      if(this.dropdownConfigurations.isMultipleSelect){
        this.form.get('treeSelect').setValue(
          this.list.filter((listItem) => {
            return listItem?.['id'] == selectedId;
          }).map(item=>item['id'])
        );
      }else {
        this.form.get('treeSelect').setValue(
          this.list.filter((listItem) => {
            return listItem?.['id'] == selectedId;
          }).map(item=>item['id'])[0]
        );
      }
    });
  }

  private onHandleBlocStates(state: TreeDropdownState): void {
    if (!state) {
      return;
    }
    if (state instanceof ProcessingState) {
      this.loading = true;
    } else if (state instanceof TreeDropdownReadyState) {
      this.loading = false;
      this.pagination = state.pagination;
      if (this.pagination?.currentPage == 1) {
        this.list = state.list;
        this.checkIfListContainsRetrievedElement();
      } else {
        this.list.push(...this.checkIfElementIsFound(state.list));
      }
    } else if (state instanceof TreeDropdownSearchReadyState) {
      this.loading = false;
      this.list = state.list;
      this.pagination = state.pagination;
    } else if (state instanceof ErrorState) {
      this.loading = false;
    }
  }

  private checkIfThereIsCallerChangeFromOutSide(): void {
    this.changeFromOutSide$.pipe(takeUntil(this.destroy$), debounceTime(10)).subscribe((change: boolean) => {
      if (change) {
        this.bloc.setEvent.next(new TreeDropdownLoadEvent(this.caller, this.page, this.limit, this.search));
      }
    });
  }

  private checkIfListContainsRetrievedElement(): void {
    if (this.retrievedElement) {
      const element = this.list.find((item) => item['id'] == this.retrievedElement['id']);
      if (!element) {
        this.list.push(this.retrievedElement);
      }
    }
  }

  private checkIfElementIsFound(list: T[]): T[] {
    if (this.retrievedElement) {
      list = list.filter((listItem) => listItem['id'] !== this.retrievedElement['id']);
    } else {
      list = list;
    }
    return list;
  }

  private getFormControl(): void {
   if(this.dropdownConfigurations.treeFormControlName) {
    this.formGroup =  this.injector.get(FormGroupDirective).form;
    this.formControl = this.formGroup?.get(this.dropdownConfigurations.treeFormControlName);
   }
  }
}
